/**
 * The Follow / Requested / Following control on another fan's profile
 * (ISSUE-1370). It renders the `followState` the server resolved and never
 * works one out from the privacy flag: whether a follow needs approval is the
 * server's rule. Undoing either state asks first, through the shared confirm.
 *
 * After a tap, one polite live line says what happened ("You're now following
 * Sam."), the inline stand-in for v1's snackbar. This app has no toasts.
 *
 * `trailing` holds the profile's other buttons (Message, More). They share
 * the follow button's row and the status line goes under the whole row, so a
 * long line at 390px never pushes them onto a second line (QA round 1).
 */
import { Button } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { followButtonLabel } from "../followers/followCopy";
import type { typeFollowState } from "../followers/followState";
import { useFollowToggle } from "../followers/useFollowToggle";

export type typeFanFollowActionProps = {
  accountId: string;
  displayName: string;
  followState: typeFollowState;
  followsYou: boolean;
  trailing?: ReactNode | undefined;
};

export const FanFollowAction = (props: typeFanFollowActionProps): ReactNode => {
  const follow = useFollowToggle({
    accountId: props.accountId,
    displayName: props.displayName,
    state: props.followState,
  });

  return (
    <div className="flex flex-col items-center gap-1 sm:items-start">
      <div
        className="flex flex-wrap items-start justify-center gap-2 sm:justify-start"
        data-testid="fan-follow-row"
      >
        <Button
          variant={follow.state === "none" ? "outline" : "secondary"}
          onClick={() => {
            void follow.toggle();
          }}
          disabled={follow.isPending}
          aria-pressed={follow.state !== "none"}
          data-testid="fan-follow-action"
          data-follow-state={follow.state}
        >
          {followButtonLabel(follow.state, { followsYou: props.followsYou })}
        </Button>
        {props.trailing}
      </div>
      <p
        role="status"
        aria-live="polite"
        className="text-center text-caption text-ink-secondary sm:text-left"
        data-testid="fan-follow-status"
      >
        {follow.announcement ?? ""}
      </p>
    </div>
  );
};
